import { MISSION_EVENT_TYPES, MISSION_STATES } from 'domain/mappers/missionFromRestV2';
import { fromPlainEnum } from 'domain/mappers/restMapping';
import type { MissionEventFE, MissionEventTypeFE, MissionFE, MissionProgressFE, MissionStateFE } from 'domain/mission';
import type { FieldError, MonitoredField } from 'domain/monitored';
import type { GeoPositionFE } from 'domain/vehicle';
import { toDate } from 'helper/time';

import type {
    ErrorV2,
    MissionEvent,
    MissionProgressDetailsFieldV2,
    MissionStateFieldV2,
    MissionV2,
    Position,
} from 'API';

const MISSION_STATE_ALIASES: Record<string, MissionStateFE> = {
    ACTIVE: 'EXECUTING',
    COMPLETED: 'COMPLETE',
};

const MISSION_EVENT_TYPE_ALIASES: Record<string, MissionEventTypeFE> = {
    MISSION_STARTED: 'MISSION_EXECUTING',
    MISSION_COMPLETED: 'MISSION_COMPLETE',
};

/**
 * A direct indexed read, **not** `raw in MISSION_STATE_ALIASES`: `'toString' in {}` is `true` for a
 * plain object literal, so an `in` guard reads the prototype chain. With `noUncheckedIndexedAccess`
 * the indexed read is already typed `MissionStateFE | undefined`, so `??` is sound and no
 * assertion is needed. `fromPlainEnum`, not `fromEnum`: the AppSync enums carry no prefix.
 */
const toMissionState = (raw: unknown): MissionStateFE => {
    const alias = typeof raw === 'string' ? MISSION_STATE_ALIASES[raw] : undefined;
    return alias ?? fromPlainEnum<MissionStateFE>(raw, MISSION_STATES);
};

/**
 * `SafeMissionV2Schema` already degrades an unrecognised event type to the literal 'UNKNOWN'
 * (`UNKNOWN_MISSION_EVENT_TYPE` in `helper/v2Schemas.ts`), and 'UNKNOWN' is a member of
 * `MISSION_EVENT_TYPES`, so that sentinel passes straight through.
 */
const toMissionEventType = (raw: unknown): MissionEventTypeFE => {
    const alias = typeof raw === 'string' ? MISSION_EVENT_TYPE_ALIASES[raw] : undefined;
    return alias ?? fromPlainEnum<MissionEventTypeFE>(raw, MISSION_EVENT_TYPES);
};

/**
 * Local on purpose — do NOT replace with `restMapping.toFieldError`. The AppSync `ErrorV2` is
 * `{ raisedAt, type }` with unprefixed `ErrorTypeV2` members, while `restMapping.toFieldError`
 * reads `updated_at` and strips an `ERROR_TYPE_` prefix AppSync never sends. `FieldErrorType` is
 * an alias of `ErrorTypeV2`, so `error.type` needs no mapping at all. This is the same deliberate
 * duplication `vehicleLiveFieldsFromV2.ts` already relies on.
 */
const toFieldError = (error?: ErrorV2 | null): FieldError | null =>
    error == null ? null : { type: error.type, updatedAt: toDate(error.raisedAt) };

/**
 * The AppSync `Position` spells its extras `heading` / `altitude`; `restMapping.toGeoPosition`
 * expects the REST wire's `heading_degrees` / `altitude_meters`, so it cannot be reused here.
 * Same shape as `positionFromV2`'s inner block in `vehicleLiveFieldsFromV2.ts`.
 */
const toGeoPositionV2 = (raw?: Position | null): GeoPositionFE | undefined => {
    if (raw == null || typeof raw.latitude !== 'number' || typeof raw.longitude !== 'number') {
        return;
    }
    const position: GeoPositionFE = { lat: raw.latitude, lng: raw.longitude };
    if (typeof raw.heading === 'number') {
        position.heading = raw.heading;
    }
    if (typeof raw.altitude === 'number') {
        position.altitudeMeters = raw.altitude;
    }
    return position;
};

const stateFromV2 = (field?: MissionStateFieldV2 | null): MonitoredField<MissionStateFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    if (field.value == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: { value: toMissionState(field.value.state), updatedAt: toDate(field.value.updatedAt) },
        error,
    };
};

const progressFromV2 = (field?: MissionProgressDetailsFieldV2 | null): MonitoredField<MissionProgressFE> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    const raw = field.value;
    if (raw == null) {
        return { ttl, value: null, error };
    }
    // Read defensively: `SafeMissionV2Schema` can degrade a corrupt field to `undefined` behind
    // the non-optional type, so trusting the declaration would throw.
    return {
        ttl,
        value: {
            value: {
                eta: toDate(raw.eta),
                remainingDistanceInMeters:
                    typeof raw.remainingDistanceInMeters === 'number' ? raw.remainingDistanceInMeters : undefined,
                traveledDistanceInMeters:
                    typeof raw.traveledDistanceInMeters === 'number' ? raw.traveledDistanceInMeters : undefined,
            },
            updatedAt: toDate(raw.updatedAt),
        },
        error,
    };
};

/**
 * One AppSync `MissionEvent` -> `MissionEventFE`. Never drops an entry: an unreadable type is
 * labelled 'UNKNOWN' and an unreadable position is simply absent, both of which the event log
 * renders. Mirrors `toMissionEvent` in `missionFromRestV2.ts` minus the per-entry zod wrapper.
 */
const eventFromV2 = (raw: MissionEvent): MissionEventFE => {
    const event: MissionEventFE = {
        type: toMissionEventType(raw.type),
        createdAt: toDate(raw.createdAt),
        lastRepeatedAt: toDate(raw.lastRepeatedAt),
    };
    const position = toGeoPositionV2(raw.position);
    if (position !== undefined) {
        event.position = position;
    }
    if (typeof raw.repeatCount === 'number') {
        event.repeatCount = raw.repeatCount;
    }
    return event;
};

/**
 * The fields a subscription snapshot is allowed to overwrite.
 *
 * There is deliberately **no `route` key**. A route can exceed the SNS message size limit and never
 * changes after creation, so the backend omits it from snapshots; a route that does arrive on a
 * push is discarded. Enforcing that through the TYPE rather than a runtime check means a future
 * contributor cannot forget a guard that does not exist — the same way `VehicleLiveFields` works.
 */
export interface MissionLiveFields {
    state: MonitoredField<MissionStateFE>;
    progressDetails: MonitoredField<MissionProgressFE>;
    timestamp: Date | null;
    actualStartTime: Date | null;
    actualArrivalTime: Date | null;
    plannedArrivalTime: Date | null;
    /** Absent when the push carried no usable list — see {@link applyMissionLiveUpdate}. */
    events?: MissionEventFE[];
}

export const missionLiveFieldsFromV2 = (live: MissionV2): MissionLiveFields => {
    const fields: MissionLiveFields = {
        state: stateFromV2(live.state),
        progressDetails: progressFromV2(live.progressDetails),
        timestamp: toDate(live.timestamp),
        actualStartTime: toDate(live.actualStartTime),
        actualArrivalTime: toDate(live.actualArrivalTime),
        plannedArrivalTime: toDate(live.plannedArrivalTime),
    };
    if (Array.isArray(live.events) && live.events.length > 0) {
        fields.events = live.events.map(eventFromV2);
    }
    return fields;
};

/** Patches the live fields of a cached `MissionFE` draft in place. Mutates, returns nothing. */
export const applyMissionLiveUpdate = (target: MissionFE, live: MissionV2): void => {
    const fields = missionLiveFieldsFromV2(live);
    target.state = fields.state;
    target.progressDetails = fields.progressDetails;
    target.timestamp = fields.timestamp;
    target.actualStartTime = fields.actualStartTime;
    target.actualArrivalTime = fields.actualArrivalTime;
    target.plannedArrivalTime = fields.plannedArrivalTime;
    // Events are append-only server-side, so a shorter list is a truncated payload, not a real
    // transition. Writing only when longer prevents a partial push from erasing history.
    // `route` has no equivalent assignment at all, by the type above.
    if (fields.events !== undefined && fields.events.length >= target.events.length) {
        target.events = fields.events;
    }
};

/**
 * Builds a WHOLE `MissionFE` from a push, for the case where the pushed mission id differs from
 * the one the REST cache holds (a newly assigned mission, or none cached yet). Writing this
 * immediately is what keeps the operator on the mission the backend just announced even when the
 * mission service read model lags behind the push, instead of leaving the panel on the previous
 * mission with nothing to retry.
 *
 * Carries **no `route` key**, for the reason stated on {@link MissionLiveFields}: the REST refetch
 * that follows, or `fetchMissingRoute`, supplies it.
 */
export const missionFromLiveV2 = (live: MissionV2): MissionFE => {
    const fields = missionLiveFieldsFromV2(live);
    return {
        id: live.id,
        vin: live.vin,
        fleetId: live.fleetId,
        name: live.name,
        description: live.description ?? undefined,
        timestamp: fields.timestamp,
        actualStartTime: fields.actualStartTime,
        actualArrivalTime: fields.actualArrivalTime,
        plannedArrivalTime: fields.plannedArrivalTime,
        events: fields.events ?? [],
        state: fields.state,
        progressDetails: fields.progressDetails,
    };
};
